import { formatNumber, formatPercent } from '@angular/common';
import {
    CmpsScoreSectionTrendData,
    CmpsScoreSectionTrendType,
} from '@cmps/ng-kit';
import { TranslateService } from '@ngx-translate/core';

import { TrendType } from '../../scorecard/core/trend-type.model';
import { OverallScore } from '../../scorecard/scorecard.model';
import { epicDependencies } from '../../store';
import { TranslationKeyMap } from '../my-scorecard.i18n';
export function getTrendData(
    adjustedScore: OverallScore | null,
    originalScore: number | null,
    trendId: TrendType,
): CmpsScoreSectionTrendData {
    const translateService =
        epicDependencies.injector.get<TranslateService>(TranslateService);
    const naLabel = translateService.instant(TranslationKeyMap._9402_NA);

    const getFormattedPercent = (diff: number): string =>
        formatPercent(diff, 'en-CA', '1.0-1');

    const getFormattedNumber = (score: number | null): string =>
        score != null ? formatNumber(score, 'en-CA', '1.0-1') : naLabel;

    // Handle TrendType.UNKNOWN
    if (trendId === TrendType.UNKNOWN) {
        return {
            trend: null,
            noTrendLabel: naLabel,
            trendValue: '-',
            trendColor: '#000',
            previousScore: '-',
        };
    }

    // Handle cases where scores are missing
    if (adjustedScore == null && originalScore == null) {
        return createDefaultTrendResponse('-', '-', '#000');
    }

    if (adjustedScore == null) {
        return createDefaultTrendResponse(
            '-',
            getFormattedNumber(originalScore),
            '#000',
        );
    }

    if (originalScore == null) {
        return createDefaultTrendResponse('-', naLabel, '#000');
    }

    // Calculate score difference
    const scoreDiff =
        (adjustedScore.scoreValue - originalScore) / originalScore;

    // Determine trend
    const { trend, trendValue, trendColor } = getTrendDetails(
        trendId,
        scoreDiff,
        getFormattedPercent,
    );

    return {
        trend,
        trendValue,
        trendColor,
        previousScore: trend == null ? '-' : getFormattedNumber(originalScore),
        noTrendLabel: trend == null ? naLabel : undefined,
    };
}

function createDefaultTrendResponse(
    trendValue: string,
    previousScore: string,
    trendColor: string,
): CmpsScoreSectionTrendData {
    return {
        trend: CmpsScoreSectionTrendType.NO_CHANGE,
        trendValue,
        trendColor,
        previousScore,
        noTrendLabel: undefined,
    };
}

function getTrendDetails(
    trendId: TrendType,
    scoreDiff: number,
    getFormattedPercent: (diff: number) => string,
): {
    trend: CmpsScoreSectionTrendType;
    trendValue: string;
    trendColor: string;
} {
    let trend = CmpsScoreSectionTrendType.NO_CHANGE;
    let trendValue = '-';
    let trendColor = '#000';

    switch (trendId) {
        case TrendType.UP:
            trend = CmpsScoreSectionTrendType.UP;
            trendValue =
                scoreDiff !== 0
                    ? `+${getFormattedPercent(scoreDiff)}`
                    : getFormattedPercent(scoreDiff);
            trendColor = '#137e0f';
            break;
        case TrendType.DOWN:
            trend = CmpsScoreSectionTrendType.DOWN;
            trendValue = getFormattedPercent(scoreDiff);
            trendColor = '#ce2521';
            break;
        case TrendType.NEUTRAL:
            trend = CmpsScoreSectionTrendType.NO_CHANGE;
            break;
    }

    return { trend, trendValue, trendColor };
}
